import React, { useEffect, useMemo } from 'react';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { useGroupChannelList } from '@sendbird/uikit-react/GroupChannelList/context';

import type { ChannelLike } from '@/components/mui/Layouts/components/Chat/types';

interface Props {
  /** Whether this list is the current-context tab, which additionally narrows rows by company/document. */
  isCtx: boolean;
  matchesCurrentContext: (channel: Pick<ChannelLike, 'cachedMetaData' | 'data'>) => boolean;
  matchesSearch: (channel: ChannelLike) => boolean;
  /** Reports whether the list is visually empty (all channels hidden by the client-side filters). */
  onEmptyChange: (isEmpty: boolean) => void;
}

/**
 * Detects when the chat list is visually empty and reports it upward via `onEmptyChange`. It renders
 * NOTHING itself — the single "No chats" placeholder is rendered by the panel (ChannelList/index.tsx)
 * as a normal sibling between the chat list and the Users section, so its divider and layout are
 * predictable (a portaled node inside Sendbird's list body could not carry a reliable separator —
 * DA-19646).
 *
 * This is the SINGLE source of truth for the empty state. Sendbird's own NO_CHANNELS placeholder is
 * hidden via CSS (GroupChannelListWrapper), so this detector covers BOTH empty cases: the genuinely-
 * empty collection (`length === 0`, e.g. a search returning zero channels) AND the filtered-empty
 * collection (channels exist but every row is hidden by the client-side context/search filters —
 * DA-19567). Mounted inside the list provider via renderHeader so it can read the same collection
 * the list renders from.
 */
const ChannelListEmptyState: React.FC<Props> = ({ isCtx, matchesCurrentContext, matchesSearch, onEmptyChange }) => {
  const { state } = useGroupChannelList();
  const { groupChannels, initialized } = state;

  const hasVisibleChannel = useMemo(
    () =>
      groupChannels.some(
        (channel: GroupChannel) => (!isCtx || matchesCurrentContext(channel)) && matchesSearch(channel),
      ),
    [groupChannels, isCtx, matchesCurrentContext, matchesSearch],
  );

  // Empty when no channel is visible — covers both length===0 (Sendbird's placeholder is hidden via
  // CSS) and length>0-but-all-filtered-out. `initialized` gates out the pre-load state.
  const showEmpty = initialized && !hasVisibleChannel;

  useEffect(() => {
    onEmptyChange(showEmpty);
  }, [showEmpty, onEmptyChange]);

  return null;
};

export default ChannelListEmptyState;
